import { useCallback, useEffect, useRef, useState } from "react";
import {
  moderationApi,
  type AdminCase,
  type ReasonCategory,
} from "../../lib/moderation-api";
import { AdminCaseList } from "./AdminCaseList";
import { AdminCaseDetail } from "./AdminCaseDetail";
import { AdminHideForm } from "./AdminHideForm";
import { cn } from "../cn";

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "MODERATED", label: "Active" },
  { key: "REVIEW_PENDING", label: "Reviews Pending" },
  { key: "RESTORED", label: "Restored" },
  { key: "INHERITED", label: "Inherited" },
];
const TARGET_FILTERS: { key: string; label: string }[] = [
  { key: "", label: "All targets" },
  { key: "territory", label: "Territories" },
  { key: "artwork", label: "Artworks" },
];

/**
 * Moderation workspace (Claudio's previously approved Command Console body):
 * summary tiles + filters + case ledger + case dossier. Unchanged behaviour —
 * lifted verbatim out of the old single-view route so the console can host both
 * Moderation and the new Review Operations under one shell.
 */
export function AdminModerationView({
  onMutated,
  hideOpen,
  onHideOpenChange,
}: {
  onMutated: () => void;
  hideOpen: boolean;
  onHideOpenChange: (v: boolean) => void;
}) {
  const [status, setStatus] = useState("");
  const [targetType, setTargetType] = useState("");
  const [q, setQ] = useState("");
  const [cases, setCases] = useState<AdminCase[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ moderated: number; review_pending: number; restored: number; awaiting_reply: number } | null>(null);
  const [reasons, setReasons] = useState<ReasonCategory[]>([]);
  const [tick, setTick] = useState(0);
  const reqRef = useRef(0);

  const refreshAll = useCallback(() => { setTick((t) => t + 1); onMutated(); }, [onMutated]);

  useEffect(() => {
    const c = new AbortController();
    moderationApi.reasonCategories(c.signal).then((r) => setReasons(r.reason_categories)).catch(() => {});
    return () => c.abort();
  }, []);

  useEffect(() => {
    const c = new AbortController();
    moderationApi.summary(c.signal).then(setSummary).catch(() => {});
    return () => c.abort();
  }, [tick]);

  useEffect(() => {
    const myId = ++reqRef.current;
    const c = new AbortController();
    setListLoading(true);
    const query = q.trim();
    const p = query
      ? moderationApi.search(query, c.signal)
      : moderationApi.listCases({ status: status || undefined, targetType: targetType || undefined, limit: 100 }, c.signal);
    p.then((res) => {
      if (myId !== reqRef.current) return;
      setCases(res.cases);
      setListLoading(false);
    }).catch(() => {
      if (c.signal.aborted || myId !== reqRef.current) return;
      setCases([]);
      setListLoading(false);
    });
    return () => c.abort();
  }, [status, targetType, q, tick]);

  return (
    <div data-testid="admin-moderation-view">
      <div data-testid="admin-summary" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryTile testid="admin-summary-active" label="Active Cases" value={summary?.moderated} accent="coral" />
        <SummaryTile testid="admin-summary-reviews" label="Reviews Pending" value={summary?.review_pending} accent="teal" />
        <SummaryTile testid="admin-summary-awaiting" label="Awaiting Reply" value={summary?.awaiting_reply} accent="coral" />
        <SummaryTile testid="admin-summary-restored" label="Restored / Closed" value={summary?.restored} accent="muted" />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div data-testid="admin-status-filters" className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              data-testid={`admin-filter-status-${f.key || "all"}`}
              data-active={status === f.key}
              onClick={() => { setStatus(f.key); setQ(""); }}
              className={cn("facet-btn h-8 px-3 text-[10px] tracking-[0.14em]", status === f.key && !q && "facet-btn--active")}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          data-testid="admin-filter-target"
          value={targetType}
          onChange={(e) => { setTargetType(e.target.value); setQ(""); }}
          className="h-8 rounded-md border border-border-soft bg-obsidian px-2 font-mono text-[10px] uppercase tracking-[0.12em] text-text-secondary focus:outline-none focus:border-border-accent"
        >
          {TARGET_FILTERS.map((t) => (
            <option key={t.key || "all"} value={t.key}>{t.label}</option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <input
            data-testid="admin-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search case / target / owner…"
            className="h-8 w-64 max-w-[60vw] rounded-md border border-border-soft bg-obsidian px-3 font-mono text-[11px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-accent"
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] gap-5 items-start">
        <AdminCaseList cases={cases} loading={listLoading} selectedId={selectedId} onSelect={setSelectedId} />
        <div data-testid="admin-detail-slot">
          {selectedId ? (
            <AdminCaseDetail key={selectedId} caseId={selectedId} onActioned={refreshAll} />
          ) : (
            <div data-testid="admin-detail-empty" className="panel-chamfer panel-chamfer--sm p-8 text-center font-mono text-[12px] text-text-muted">
              Select a case to inspect its timeline, evidence and actions.
            </div>
          )}
        </div>
      </div>

      {hideOpen && (
        <AdminHideForm
          reasons={reasons}
          onClose={() => onHideOpenChange(false)}
          onCreated={(caseId) => { onHideOpenChange(false); setSelectedId(caseId); refreshAll(); }}
        />
      )}
    </div>
  );
}

function SummaryTile({
  testid,
  label,
  value,
  accent,
}: {
  testid: string;
  label: string;
  value: number | undefined;
  accent: "coral" | "teal" | "muted";
}) {
  const color = accent === "coral" ? "var(--neon-coral)" : accent === "teal" ? "var(--spec-cyan)" : "var(--text-muted)";
  return (
    <div data-testid={testid} className="panel-chamfer panel-chamfer--sm px-4 py-3.5">
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">{label}</div>
      <div className="mt-1 font-display text-[28px] font-semibold tabular-nums" style={{ color }}>
        {value ?? "—"}
      </div>
    </div>
  );
}
